import React, { Component } from "react";
import PropTypes from "prop-types";
import Alert from "../../common/alert/Alert";
import { FormControl, Accordion } from "react-bootstrap";
import AppModal from "../../common/modal/AppModal";
import ToggleSwitch from "@trendmicro/react-toggle-switch";
import i18n from "i18next";
import "./_exportModal.scss";
import spinner from "../../../../dist/images/spinner.svg";
import { templatesSliDoc, templatesSli, templatesDoc } from "./templates/templates";
import TemplateThumbnailPrint from "./TemplateThumbnailPrint";
import { createBox } from "../../../../common/common_tools";
import {
  fetchTrainingShareInfo,
  patchTrainingShareOpen,
  fetchTrainingShareUsers,
  addTrainingShareUser,
  deleteTrainingShareUser,
} from "../../../../common/server_utils";
// V1 (VideoExportTab) deprecated — Video format now always renders V2.
// File retained on disk for the rollback window; remove once V2 is stable.
import VideoExportTabV2 from "./VideoExportTabV2";
import SaveRequiredGate from "./SaveRequiredGate";
import "./_videoExportV2.scss";
import { isClmCourse } from "../../../../common/clm";
import { buildClmZipName, bumpClmVersion, sanitizeClmZipFileName } from "./clm/clmResolvers";

/**
 * Export course modal
 */
export default class ExportModal extends Component {
  constructor(props) {
    super(props);
    this.templatesSliDoc = templatesSliDoc();
    this.templatesSli = templatesSli();
    this.templatesDoc = templatesDoc();
    this.state = {
      // videoOnly mode opens the modal straight on the Video tab and hides
      // the format-picker grid. Used by the standalone "Video Export"
      // menu button — Video is a fundamentally different operation from
      // SCORM/PDF/URL exports and deserves its own entry point.
      // Format is keyed by NAME (never by index): the CLM card is prepended
      // only for CLM courses, which would shift every index-based check.
      format: props.videoOnly ? "VIDEO" : isClmCourse(props.globalConfig) ? "CLM" : "SCORM 2004",
      showLoader: false,
      selfContained: false,
      subtitlesOnByDefault: !!(props.globalConfig && props.globalConfig.subtitlesOnByDefault),
      showAlert: false,
      showPrintAlert: false,
      forcePageBreak: false,
      slidesPerPage: 2,
      slidesWithComments: false,
      optionName: "fullSlideDoc",
      drawBorder: true,
      explanation: i18n.t("export.full_sli_doc"),
      landscape: false,
      itemSelected: 0,
      settingType: 1,
      exportUrlVersion: "",
      shareIsPublic: true,
      shareUsers: [],
      shareNewEmail: "",
      shareLoading: false,
      shareSaving: false,
      shareAdding: false,
      // Save-required gate: shown before the Video tab can start an
      // export. Asks the user to save first if there are dirty changes
      // so the worker sees the latest exportedState in the DB.
      showSaveRequired: false,
      clmZipName: buildClmZipName(props.globalConfig),
      // null = not validated yet (Export stays disabled until validateClm ran).
      clmProblems: null,
      // Set after a successful export that carried result.warnings (e.g.
      // missing_asset/unsupported_plugin/external_html found only during the
      // serialize pass, which the pre-export static validateClm() can't see).
      clmExportDone: false,
      // Scene picked for "SCORM_SCENES" ("" = all scenes); preset on open.
      sceneId: "",
    };
  }

  async _loadShareData() {
    const trainingId = this.props.trainingId || window.idealStudioTrainingId;
    if (!trainingId) return;
    this.setState({ shareLoading: true });
    const [info, users] = await Promise.all([
      fetchTrainingShareInfo(trainingId),
      fetchTrainingShareUsers(trainingId),
    ]);
    this.setState({
      shareIsPublic: info ? info.isPublic !== false : true,
      shareUsers: Array.isArray(users) ? users : [],
      shareLoading: false,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.format === "URL" && prevState.format !== "URL") {
      this._loadShareData();
    }
    if (this.props.show && !prevProps.show) {
      if (this.props.getExportScenes) {
        this.setState({ sceneId: this.props.getExportScenes().defaultId });
      }
      if (this.state.format === "URL") this._loadShareData();
      // The constructor runs when the nav bar mounts — before the course's
      // globalConfig (and therefore .clm) has loaded — so its CLM default never
      // fires. Apply it when the modal actually opens instead.
      if (!this.props.videoOnly && isClmCourse(this.props.globalConfig)) {
        this.setState({ format: "CLM" });
        this._refreshClm();
      }
    }
  }

  // Re-derive the zip name (version may have bumped) and re-run the static
  // validation every time the modal opens or the user asks for a re-check.
  _refreshClm() {
    this.setState({
      clmZipName: buildClmZipName(this.props.globalConfig),
      clmProblems: this.props.validateClm ? this.props.validateClm() : null,
      clmExportDone: false,
    });
  }

  _clmProblemLine(problem, index) {
    return (
      <li key={index}>
        {i18n.t("export.clm_problem_" + problem.code, {
          defaultValue: problem.code,
          scene: problem.scene || "",
          plugin: problem.plugin || "",
          connectMode: problem.connectMode || "",
          url: problem.url || "",
          detail: problem.detail || "",
        })}
      </li>
    );
  }

  // SaveRequiredGate-style blocking list rendered INSIDE the modal body (no
  // popup over popup). Errors block the Export button; warnings do not.
  _renderClmProblems() {
    const problems = this.state.clmProblems;
    const recheck = (
      <button type="button" className="url-share-add-btn" onClick={() => this._refreshClm()}>
        <i className="material-icons">refresh</i>
        {i18n.t("export.clm_check_again")}
      </button>
    );
    if (!problems) {
      return (
        <div className="clm-problems">
          <div className="clm-problems__group clm-problems__group--warn">
            {i18n.t("export.clm_not_checked")}
          </div>
          {recheck}
        </div>
      );
    }
    return (
      <div className="clm-problems">
        {problems.errors.length === 0 ? (
          <div className="clm-problems__ok">
            <i className="material-icons">check_circle</i>
            {i18n.t("export.clm_no_problems")}
          </div>
        ) : (
          <div className="clm-problems__group clm-problems__group--error">
            <div className="clm-problems__title">{i18n.t("export.clm_errors_title")}</div>
            <ul>{problems.errors.map((p, i) => this._clmProblemLine(p, i))}</ul>
          </div>
        )}
        {problems.warnings.length > 0 ? (
          <div className="clm-problems__group clm-problems__group--warn">
            <div className="clm-problems__title">{i18n.t("export.clm_warnings_title")}</div>
            <ul>{problems.warnings.map((p, i) => this._clmProblemLine(p, i))}</ul>
          </div>
        ) : null}
        {recheck}
      </div>
    );
  }

  render() {
    const cancelEvent = new Event("canceled");

    const callback = (fail) => {
      this.setState({ showLoader: false });
      if (fail === "nullPrint") {
        this.setState({ showLoader: false, showPrintAlert: true });
      } else if (fail === "downloadFailed") {
        if (typeof window.showServerModal === "function") {
          window.showServerModal(
            i18n.t("messages.error"),
            i18n.t("export.download_failed", "İndirme tamamlanamadı"),
            false
          );
        }
        this.props.close(true);
      } else if (fail) {
        this.setState({ showAlert: true });
      } else {
        this.props.close(true);
      }
    };

    // Builder failures that carry a problems list re-render the in-modal list
    // (validation / download / artifact / parity); anything else falls through
    // to the generic export error handling. Success = D9 version bump + save.
    const clmCallback = (err, result) => {
      if (err && err.code === "cancelled") {
        // User-initiated cancel (Discard already closed the modal and reset
        // showLoader synchronously) — swallow it so this stale rejection
        // doesn't fall through to the generic handler and set showAlert on
        // an already-closed modal, which would surface as a spurious error
        // the next time Export is opened.
        this.setState({ showLoader: false });
        return;
      }
      if (err && err.problems) {
        this.setState({
          showLoader: false,
          clmProblems: {
            errors: err.problems,
            warnings: err.warnings || (this.state.clmProblems || {}).warnings || [],
          },
        });
        return;
      }
      if (err) {
        callback(err);
        return;
      }
      if (this.props.changeGlobalConfig) {
        this.props.changeGlobalConfig("clm", bumpClmVersion(this.props.globalConfig.clm));
      }
      if (typeof this.props.onSave === "function") this.props.onSave();
      // result.warnings can carry problems found only during the serialize
      // pass (missing_asset/unsupported_plugin/external_html) that the
      // pre-export validateClm() static check never sees. The zip has
      // already downloaded — keep the modal open to show them instead of
      // silently discarding them via the generic auto-close success path.
      const warnings = (result && result.warnings) || [];
      if (warnings.length > 0) {
        this.setState({
          showLoader: false,
          clmExportDone: true,
          clmProblems: { errors: [], warnings },
        });
        return;
      }
      callback();
    };

    // Images the package builder could not download are left out of the zip, not fatal.
    const warnSkipped = (skipped) => {
      if (skipped && skipped.length && window.showServerModal) {
        window.showServerModal(
          i18n.t("messages.error"),
          i18n.t("export.assets_skipped") + skipped.join(", "),
          false
        );
      }
    };
    const withSkipped = (fail, skipped) => {
      callback(fail);
      if (!fail) warnSkipped(skipped);
    };

    const isSafari =
      /constructor/i.test(window.HTMLElement) ||
      (function (p) {
        return p.toString() === "[object SafariRemoteNotification]";
      })(!window.safari || (typeof safari !== "undefined" && safari.pushNotification));
    const isFirefox = typeof InstallTrigger !== "undefined";
    const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
    const aspectRatio = this.props.aspectRatio;

    const exportFormats = [
      {
        format: "SCORM 2004",
        icon: "school",
        desc: "LMS uyumlu paket",
        handler: () => this.props.scorm(true, withSkipped, this.state.selfContained),
      },
      {
        format: "SCORM 1.2",
        icon: "school",
        desc: "Eski LMS sistemleri",
        handler: () => this.props.scorm(false, withSkipped, this.state.selfContained),
      },
      {
        format: "SCORM_SCENES",
        icon: "library_books",
        desc: i18n.t("export.scorm_scenes_desc"),
        formatRender: i18n.t("export.scorm_scenes"),
        handler: () =>
          this.props.scorm(
            true,
            (fail, failed, skipped) => {
              this.setState({ sceneProgress: null });
              callback(fail);
              if (!fail && failed && failed.length && window.showServerModal) {
                window.showServerModal(
                  i18n.t("messages.error"),
                  i18n.t("export.scorm_scenes_failed") + failed.join(", "),
                  false
                );
              } else if (!fail) {
                warnSkipped(skipped);
              }
            },
            this.state.selfContained,
            true,
            (k, n) => this.setState({ sceneProgress: i18n.t("export.scene_progress", { k, n }) }),
            this.state.sceneId || undefined
          ),
      },
      {
        format: "URL",
        icon: "link",
        desc: "Paylaşılabilir bağlantı",
        handler: () =>
          this.props.export("URL", callback, this.state.selfContained, this.state.exportUrlVersion),
      },
      /*
      {
        format: "HTML",
        icon: "code",
        desc: "Web sayfası olarak",
        handler: () => this.props.export("HTML", callback, this.state.selfContained),
      },
      {
        format: "PDF",
        icon: "picture_as_pdf",
        desc: "Yazdırılabilir belge",
        formatRender: (
          <span>
            PDF <sub className="betaSub">BETA</sub>
          </span>
        ),
        handler: () => this.props.export("PDF", callback, this.state),
      }
     */
    ];

    if (isClmCourse(this.props.globalConfig)) {
      exportFormats.unshift({
        format: "CLM",
        icon: "slideshow",
        desc: i18n.t("export.clm_desc"),
        formatRender: (
          <span>
            CLM <sub className="betaSub">BETA</sub>
          </span>
        ),
        handler: () => this.props.exportClm(this.state.clmZipName, clmCallback),
      });
    }
    const fmt = this.state.format;
    const isScorm = fmt === "SCORM 2004" || fmt === "SCORM 1.2" || fmt === "SCORM_SCENES";
    const isUrl = fmt === "URL";
    const clmBlocked =
      fmt === "CLM" && (!this.state.clmProblems || this.state.clmProblems.errors.length > 0);
    // Success-with-warnings state: the zip already downloaded, so the
    // footer swaps to a single Close button instead of Discard/Export.
    const clmExportDone = fmt === "CLM" && this.state.clmExportDone;

    const footerContent =
      fmt === "VIDEO" ? null : clmExportDone ? (
        <div className="export-modal-footer">
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={() => this.props.close(true)}
          >
            {i18n.t("export.clm_close")}
          </button>
        </div>
      ) : (
        <div className="export-modal-footer">
          {this.state.showLoader && <img className="spinnerFloat" src={spinner} />}
          {this.state.showLoader && this.state.sceneProgress}
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={(e) => {
              this.setState({ showLoader: false });
              this.props.close();
              e.preventDefault();
              document.body.dispatchEvent(cancelEvent);
            }}
          >
            {i18n.t("global_config.Discard")}
          </button>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            id="accept_export_to_scorm"
            disabled={clmBlocked}
            onClick={(e) => {
              this.setState({ showLoader: true });
              exportFormats.find((f) => f.format === fmt).handler();
              e.preventDefault();
            }}
          >
            {i18n.t("messages.export_course")}
          </button>
        </div>
      );

    // Video tab is now V2-only — always opens at 80vw with the
    // scene-strip + big-preview layout.
    const isVideoTab = fmt === "VIDEO";
    const modalSize = isVideoTab ? "xl" : "lg";
    const modalMaxWidth = isVideoTab ? "80vw" : undefined;
    const modalClassName = isVideoTab
      ? "exportoScormModalBody export-modal--manual-merge"
      : "exportoScormModalBody";

    return (
      <AppModal
        show={this.props.show}
        onClose={this.props.close}
        title={
          this.props.videoOnly
            ? i18n.t("video_export.title", { defaultValue: "Video Export" })
            : i18n.t("messages.export_course")
        }
        className={modalClassName}
        size={modalSize}
        maxWidth={modalMaxWidth}
        backdrop="static"
        footer={footerContent}
        footerProps={{ className: "export-modal-footer-wrapper" }}
        bodyProps={{ className: "export-modal-body" }}
      >
        <form className="export-modal-form">
          {this.state.showAlert ? (
            <Alert
              className="pageModal"
              show
              hasHeader
              acceptButtonText={i18n.t("messages.OK")}
              title={
                <span>
                  <i style={{ fontSize: "14px", marginRight: "5px" }} className="material-icons">
                    warning
                  </i>
                  {i18n.t("messages.error")}
                </span>
              }
              onClose={() => this.setState({ showAlert: false, showLoader: false })}
            >
              <span>{i18n.t("error.generic")}</span>
            </Alert>
          ) : null}

          {this.state.showPrintAlert ? (
            <Alert
              className="pageModal"
              show
              hasHeader
              acceptButtonText={i18n.t("messages.OK")}
              title={
                <span>
                  <i style={{ fontSize: "14px", marginRight: "5px" }} className="material-icons">
                    warning
                  </i>
                  {i18n.t("messages.error")}
                </span>
              }
              onClose={() => this.setState({ showPrintAlert: false, showLoader: false })}
            >
              <div>
                <p>
                  <b>{i18n.t("export.null_print")}</b>
                </p>
                <ul className="print-settings-explanation">
                  <li className="error-setting">
                    <span className="setting-title">{i18n.t("export.sli_doc")}:</span>{" "}
                    <span>{i18n.t("export.sli_doc_explanation")}</span>
                  </li>
                  <li className="error-setting">
                    <span className="setting-title">{i18n.t("export.sli")}:</span>{" "}
                    <span>{i18n.t("export.sli_explanation")}</span>
                  </li>
                  <li className="error-setting">
                    <span className="setting-title">{i18n.t("export.doc")}:</span>{" "}
                    <span>{i18n.t("export.doc_explanation")}</span>
                  </li>
                </ul>
              </div>
            </Alert>
          ) : null}

          {/* Format picker — hidden in videoOnly mode (separate menu item
              opens straight on the Video tab). */}
          {this.props.videoOnly ? null : (
            <div className="export-section">
              <div className="export-section-label">{i18n.t("messages.export_to_label")}</div>
              <div className="export-format-grid">
                {exportFormats.map((fmt_) => (
                  <label
                    key={fmt_.format}
                    className={`export-format-card ${fmt === fmt_.format ? "active" : ""}`}
                    onClick={() => this.setState({ format: fmt_.format })}
                  >
                    <input
                      type="radio"
                      name="radioGroup"
                      checked={fmt === fmt_.format}
                      onChange={() => this.setState({ format: fmt_.format })}
                    />
                    <i className="material-icons">{fmt_.icon}</i>
                    <span>{fmt_.formatRender || fmt_.format}</span>
                    <small>{fmt_.desc}</small>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Detaylar */}
          <div className="export-details">
            {isScorm ? <p className="export-explanation">{i18n.t("SCORM Explanation")}</p> : null}
            {isUrl ? (
              <p
                className="export-explanation"
                dangerouslySetInnerHTML={{ __html: i18n.t("URL Explanation") }}
              />
            ) : null}

            {/* CLM — zip-name input + blocking validation list, or the
                success-with-warnings summary once exportClm has completed. */}
            {fmt === "CLM" ? (
              <div className="clm-export-settings">
                {clmExportDone ? (
                  <div className="clm-problems">
                    <div className="clm-problems__ok">
                      <i className="material-icons">check_circle</i>
                      {i18n.t("export.clm_export_done", {
                        count: this.state.clmProblems.warnings.length,
                      })}
                    </div>
                    <div className="clm-problems__group clm-problems__group--warn">
                      <div className="clm-problems__title">
                        {i18n.t("export.clm_warnings_title")}
                      </div>
                      <ul>
                        {this.state.clmProblems.warnings.map((p, i) => this._clmProblemLine(p, i))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="export-explanation">{i18n.t("export.clm_explanation")}</p>
                    <label className="clm-zip-name">
                      <span>{i18n.t("export.clm_zip_name")}</span>
                      <FormControl
                        type="text"
                        value={this.state.clmZipName}
                        onChange={(e) => this.setState({ clmZipName: e.target.value })}
                        onBlur={() =>
                          this.setState((s) => ({
                            clmZipName: sanitizeClmZipFileName(s.clmZipName),
                          }))
                        }
                      />
                    </label>
                    {this._renderClmProblems()}
                  </>
                )}
              </div>
            ) : null}

            {/* URL paylaşım ayarları */}
            {isUrl ? (
              <div className="url-share-settings">
                <div className="url-share-visibility">
                  <div className="url-share-visibility-options">
                    <label
                      className={`url-visibility-card ${this.state.shareIsPublic ? "active" : ""}`}
                      onClick={async () => {
                        if (this.state.shareIsPublic === true || this.state.shareSaving) return;
                        this.setState({ shareSaving: true, shareIsPublic: true });
                        const trainingId = this.props.trainingId || window.idealStudioTrainingId;
                        await patchTrainingShareOpen(trainingId, true);
                        this.setState({ shareSaving: false });
                      }}
                    >
                      <input
                        type="radio"
                        name="shareVisibility"
                        checked={this.state.shareIsPublic === true}
                        onChange={() => {}}
                      />
                      {this.state.shareSaving && this.state.shareIsPublic === true ? (
                        <i className="material-icons url-share-spin">autorenew</i>
                      ) : (
                        <i className="material-icons">public</i>
                      )}
                      <span>Public</span>
                      <small>Herkes erişebilir</small>
                    </label>
                    <label
                      className={`url-visibility-card ${!this.state.shareIsPublic ? "active" : ""}`}
                      onClick={async () => {
                        if (this.state.shareIsPublic === false || this.state.shareSaving) return;
                        this.setState({ shareSaving: true, shareIsPublic: false });
                        const trainingId = this.props.trainingId || window.idealStudioTrainingId;
                        await patchTrainingShareOpen(trainingId, false);
                        this.setState({ shareSaving: false });
                      }}
                    >
                      <input
                        type="radio"
                        name="shareVisibility"
                        checked={this.state.shareIsPublic === false}
                        onChange={() => {}}
                      />
                      {this.state.shareSaving && this.state.shareIsPublic === false ? (
                        <i className="material-icons url-share-spin">autorenew</i>
                      ) : (
                        <i className="material-icons">lock</i>
                      )}
                      <span>Secret</span>
                      <small>{i18n.t("InvitedOnly") || "Sadece davet edilenler"}</small>
                    </label>
                  </div>
                </div>

                {!this.state.shareIsPublic && (
                  <div className="url-share-users">
                    <div className="url-share-users-title">
                      {i18n.t("SharedUsers") || "Paylaşılan Kullanıcılar"}
                    </div>
                    {this.state.shareLoading ? (
                      <div className="url-share-loading">
                        <i className="material-icons url-share-spin">autorenew</i>
                        {i18n.t("Loading") || "Yükleniyor..."}
                      </div>
                    ) : (
                      <>
                        <div className="url-share-user-list">
                          {this.state.shareUsers.length === 0 && (
                            <div className="url-share-empty">
                              {i18n.t("NoUsersAddedYet") || "Henüz kullanıcı eklenmedi"}
                            </div>
                          )}
                          {this.state.shareUsers.map((user) => (
                            <div key={user.id} className="url-share-user-row">
                              <i className="material-icons">person</i>
                              <span>{user.email}</span>
                              <button
                                type="button"
                                className="url-share-remove-btn"
                                title={i18n.t("Remove") || "Kaldır"}
                                onClick={async () => {
                                  const trainingId =
                                    this.props.trainingId || window.idealStudioTrainingId;
                                  await deleteTrainingShareUser(trainingId, user.id);
                                  const users = await fetchTrainingShareUsers(trainingId);
                                  this.setState({ shareUsers: Array.isArray(users) ? users : [] });
                                }}
                              >
                                <i className="material-icons">close</i>
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="url-share-add-row">
                          <FormControl
                            type="email"
                            placeholder="E-posta adresi girin"
                            value={this.state.shareNewEmail}
                            onChange={(e) => this.setState({ shareNewEmail: e.target.value })}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const email = this.state.shareNewEmail.trim();
                                if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                                this.setState({ shareAdding: true });
                                const trainingId =
                                  this.props.trainingId || window.idealStudioTrainingId;
                                await addTrainingShareUser(trainingId, email);
                                const users = await fetchTrainingShareUsers(trainingId);
                                this.setState({
                                  shareUsers: Array.isArray(users) ? users : [],
                                  shareNewEmail: "",
                                  shareAdding: false,
                                });
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="url-share-add-btn"
                            disabled={this.state.shareAdding}
                            onClick={async () => {
                              const email = this.state.shareNewEmail.trim();
                              if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                              this.setState({ shareAdding: true });
                              const trainingId =
                                this.props.trainingId || window.idealStudioTrainingId;
                              await addTrainingShareUser(trainingId, email);
                              const users = await fetchTrainingShareUsers(trainingId);
                              this.setState({
                                shareUsers: Array.isArray(users) ? users : [],
                                shareNewEmail: "",
                                shareAdding: false,
                              });
                            }}
                          >
                            {this.state.shareAdding ? (
                              <i className="material-icons url-share-spin">autorenew</i>
                            ) : (
                              <>
                                <i className="material-icons">add</i>
                                {i18n.t("Add") || "Ekle"}
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* Scene picker for "scene by scene": one scene, or all of them. */}
            {fmt === "SCORM_SCENES" && this.props.getExportScenes ? (
              <label className="clm-zip-name">
                <span>{i18n.t("export.scorm_scene_pick")}</span>
                <FormControl
                  as="select"
                  value={this.state.sceneId}
                  onChange={(e) => this.setState({ sceneId: e.target.value })}
                >
                  {this.props.getExportScenes().scenes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.index}. {s.name}
                    </option>
                  ))}
                  <option value="">{i18n.t("export.scorm_scene_all")}</option>
                </FormControl>
              </label>
            ) : null}

            {/* Self-contained toggle (SCORM/HTML) */}
            {isScorm ? (
              <div className="selfContained">
                <div>
                  <ToggleSwitch
                    onChange={() => this.setState({ selfContained: !this.state.selfContained })}
                    checked={this.state.selfContained}
                  />
                </div>
                <div>{i18n.t("messages.selfContained")}</div>
              </div>
            ) : null}

            {/* Persisted to globalConfig so the Visor auto-enables subtitles. */}
            {isScorm || isUrl ? (
              <div className="selfContained">
                <div>
                  <ToggleSwitch
                    onChange={() => {
                      const next = !this.state.subtitlesOnByDefault;
                      this.setState({ subtitlesOnByDefault: next });
                      if (this.props.changeGlobalConfig) {
                        this.props.changeGlobalConfig("subtitlesOnByDefault", next);
                      }
                    }}
                    checked={this.state.subtitlesOnByDefault}
                  />
                </div>
                <div>{i18n.t("messages.subtitlesOnByDefault")}</div>
              </div>
            ) : null}

            {/* PDF ayarları */}
            {fmt === "PDF" ? (
              <div>
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      {i18n.t("messages.slides_and_pages")}
                      <em className="material-icons expandArrow">expand_more</em>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="pageTemplates">
                        {this.templatesSliDoc.map((item, index) => {
                          const border =
                            this.state.itemSelected === index && this.state.settingType === 1
                              ? "solid #a963f5 3px"
                              : "solid #eee 1px";
                          return (
                            <div
                              key={index}
                              className="template_item"
                              style={{
                                position: "relative",
                                border,
                                width: index === 0 || index === 2 ? "110px" : "80px",
                                height: index === 0 || index === 2 ? "80px" : "110px",
                              }}
                            >
                              <TemplateThumbnailPrint
                                key={index}
                                index={index}
                                onClick={() => {
                                  this.setState({ itemSelected: index });
                                  if (index === 0)
                                    this.setState({
                                      slidesPerPage: 1,
                                      slidesWithComments: false,
                                      settingType: 1,
                                      optionName: "fullSlideDoc",
                                      explanation: i18n.t("export.full_sli_doc"),
                                      landscape: true,
                                    });
                                  else if (index === 1)
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 1,
                                      optionName: "twoSlideDoc",
                                      explanation: i18n.t("export.two_sli_doc"),
                                      landscape: false,
                                    });
                                  else
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 1,
                                      optionName: "defaultOption",
                                    });
                                }}
                                boxes={item.boxes}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>
                      {i18n.t("messages.only_slides")}
                      <em className="material-icons expandArrow">expand_more</em>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="pageTemplates">
                        {this.templatesSli.map((item, index) => {
                          const border =
                            this.state.itemSelected === index && this.state.settingType === 2
                              ? "solid #a963f5 3px"
                              : "solid #eee 1px";
                          const show = !((index === 0 && !isChrome) || (index === 4 && isFirefox));
                          const w = isChrome
                            ? index === 0
                              ? "100px"
                              : index === 1 || index === 4
                                ? "100px"
                                : "70px"
                            : index === 0 || index === 1 || index === 4
                              ? "110px"
                              : "80px";
                          const h = isChrome
                            ? index === 0
                              ? aspectRatio === 16 / 9
                                ? "60px"
                                : "75px"
                              : index === 1 || index === 4
                                ? "70px"
                                : "100px"
                            : index === 0 || index === 1 || index === 4
                              ? "80px"
                              : "110px";
                          return (
                            <div
                              key={index}
                              className="template_item"
                              style={{
                                display: show ? "flex" : "none",
                                position: "relative",
                                border,
                                width: w,
                                height: h,
                              }}
                            >
                              <TemplateThumbnailPrint
                                key={index}
                                index={index}
                                onClick={() => {
                                  this.setState({ itemSelected: index });
                                  if (index === 0)
                                    this.setState({
                                      slidesPerPage: 1,
                                      slidesWithComments: false,
                                      settingType: 2,
                                      optionName: "fullSlideCustom",
                                      explanation: i18n.t("export.full_sli"),
                                      landscape: true,
                                    });
                                  else if (index === 1)
                                    this.setState({
                                      slidesPerPage: 1,
                                      slidesWithComments: false,
                                      settingType: 2,
                                      optionName: "fullSlide",
                                      explanation: i18n.t("export.full_sli"),
                                      landscape: true,
                                    });
                                  else if (index === 2)
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 2,
                                      optionName: "twoSlide",
                                      explanation: i18n.t("export.two_sli"),
                                      landscape: false,
                                    });
                                  else if (index === 3)
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: true,
                                      settingType: 2,
                                      optionName: "slideComments",
                                      explanation: i18n.t("export.sli_comments"),
                                      landscape: false,
                                    });
                                  else if (index === 4)
                                    this.setState({
                                      slidesPerPage: 4,
                                      slidesWithComments: false,
                                      settingType: 2,
                                      optionName: "fourSlide",
                                      explanation: i18n.t("export.four_sli"),
                                      landscape: true,
                                    });
                                  else
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 2,
                                      optionName: "defaultOption",
                                      explanation: i18n.t("export.full_sli"),
                                    });
                                }}
                                boxes={item.boxes}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>
                      {i18n.t("messages.only_pages")}
                      <em className="material-icons expandArrow">expand_more</em>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="pageTemplates">
                        {this.templatesDoc.map((item, index) => {
                          const border =
                            this.state.itemSelected === index && this.state.settingType === 3
                              ? "solid #a963f5 3px"
                              : "solid #eee 1px";
                          return (
                            <div
                              key={index}
                              className="template_item"
                              style={{
                                display: (isSafari || isFirefox) && index === 1 ? "none" : "flex",
                                position: "relative",
                                border,
                                width: index % 2 === 1 ? "110px" : "80px",
                                height: index % 2 === 1 ? "80px" : "110px",
                              }}
                            >
                              <TemplateThumbnailPrint
                                key={index}
                                index={index}
                                onClick={() => {
                                  this.setState({ itemSelected: index });
                                  if (index === 0)
                                    this.setState({
                                      slidesPerPage: 1,
                                      slidesWithComments: false,
                                      settingType: 3,
                                      optionName: "fullDoc",
                                      explanation: i18n.t("export.full_doc"),
                                      landscape: false,
                                    });
                                  else if (index === 1)
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 3,
                                      optionName: "twoDoc",
                                      explanation: i18n.t("export.two_doc"),
                                      landscape: true,
                                    });
                                  else
                                    this.setState({
                                      slidesPerPage: 2,
                                      slidesWithComments: false,
                                      settingType: 3,
                                      optionName: "defaultOption",
                                    });
                                }}
                                boxes={item.boxes}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                <div className="print-explanation">
                  <div className="print-explanation-title">
                    <em className="material-icons printer-icon">print</em>
                    {i18n.t("export.print_title")}
                  </div>
                  <div className="print-explanation-body">{this.state.explanation}</div>
                </div>
                {isSafari || isFirefox ? (
                  <div className="browser-explanation">
                    <div className="browser-explanation-title">
                      <em className="material-icons warning-icon">warning</em> Warning
                    </div>
                    {this.state.landscape ? (
                      isSafari ? (
                        <div>{i18n.t("export.safari_landscape")}</div>
                      ) : (
                        <div>{i18n.t("export.firefox_landscape")}</div>
                      )
                    ) : isSafari ? (
                      <div>{i18n.t("export.safari_portrait")}</div>
                    ) : (
                      <div>{i18n.t("export.firefox_portrait")}</div>
                    )}
                  </div>
                ) : null}
                <div className="selfContained">
                  <div>
                    <ToggleSwitch
                      onChange={() => this.setState({ drawBorder: !this.state.drawBorder })}
                      checked={this.state.drawBorder}
                    />
                  </div>
                  <div>{i18n.t("messages.draw_borders")}</div>
                </div>
              </div>
            ) : null}

            {/* Video tab — V2-only. Gated on a save-required check so the
                worker reads the latest exportedState from the DB; if the
                editor has dirty changes (or has never saved), the gate
                blocks the popup with a "save first" CTA. */}
            {fmt === "VIDEO" ? (
              <div className="video-export-host">
                <SaveRequiredGate
                  trainingId={this.props.trainingId || window.idealStudioTrainingId}
                  isDirty={this.props.isDirty}
                  onSave={this.props.onSave}
                >
                  <VideoExportTabV2
                    trainingId={this.props.trainingId || window.idealStudioTrainingId}
                    trainingName={this.props.trainingName}
                    onClose={this.props.close}
                  />
                </SaveRequiredGate>
              </div>
            ) : null}
          </div>
        </form>
      </AppModal>
    );
  }
}

ExportModal.propTypes = {
  show: PropTypes.bool,
  export: PropTypes.func.isRequired,
  scorm: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  trainingId: PropTypes.string,
  globalConfig: PropTypes.object,
  changeGlobalConfig: PropTypes.func,
  // CLM export — only wired for the main modal; both read the store
  // at call time (EditorApp).
  exportClm: PropTypes.func,
  validateClm: PropTypes.func,
  // () => ({ scenes: [{id,name,index}], defaultId }) — scene picker for "scene by scene".
  getExportScenes: PropTypes.func,
  // Optional — wire to the editor's Redux undoable dirty flag and the
  // existing save action so the Video tab's save-required gate can
  // resolve itself without round-tripping through the user.
  isDirty: PropTypes.bool,
  onSave: PropTypes.func,
  // When true, the modal opens directly on the Video tab and hides the
  // format-selection grid. The "Video Export" nav menu item uses this.
  videoOnly: PropTypes.bool,
};
